<template>
  <div class="feedback-view">
    <h1>Give Us Your Feedback</h1>

    <input v-model="name" type="text" placeholder="Your Name" required />
    <input v-model="email" type="email" placeholder="Your Email" required />

    <div class="phone-input">
      <select v-model="countryCode" required>
        <option v-for="(code, country) in countryCodes" :key="country" :value="code">
          {{ country }} ({{ code }})
        </option>
      </select>
      <input
        v-model="mobile"
        :pattern="mobilePattern"
        placeholder="Mobile Number"
        required
      />
    </div>

    <textarea v-model="message" placeholder="Write your feedback here..." required></textarea>

    <button @click="handleSubmit">Submit</button>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { submitFeedback } from '@/services/feedbackService.js';

const name = ref('');
const email = ref('');
const mobile = ref('');
const countryCode = ref('+91');
const message = ref('');

const countryCodes = {
  'India': '+91',
  'United States': '+1',
  'United Kingdom': '+44',
  'Japan': '+81',
  'Chile': '+56',
  'Argentina': '+54',
  'Russia': '+7',
  'Australia': '+61',
  'Brazil': '+55',
  'France': '+33',
  'Spain': '+34',
  'Germany': '+49',
  'China': '+86',
  'South Korea': '+82',
  'Pakistan': '+92',
  'Hong Kong': '+852',
  'Romania': '+40',
  'Peru': '+51',
  'Czech Republic': '+420',
  'Belgium': '+32',
  'South Africa': '+27',
  'New Zealand': '+64',
  'Poland': '+48',
  'Bulgaria': '+359',
  'Sweden': '+46',
  'Norway': '+47',
  'Ukraine': '+380'
};

const mobilePattern = computed(() => '^\\d{6,15}$');

const handleSubmit = async () => {
  if (
    !name.value.trim() ||
    !email.value.trim() ||
    !mobile.value.trim() ||
    !message.value.trim()
  ) {
    alert('❌ Please fill out all fields.');
    return;
  }
  try {
    await submitFeedback(
      name.value,
      email.value,
      mobile.value,
      message.value,
      countryCode.value
    );
    alert('✅ Thank you for your feedback!');
    name.value = '';
    email.value = '';
    mobile.value = '';
    message.value = '';
  } catch (error) {
    alert('❌ Failed to submit feedback: ' + (error.message || error));
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Orbitron:wght@600&display=swap');

.feedback-view {
  max-width: 620px;
  margin: 100px auto 60px;
  padding: 40px 36px;
  text-align: center;
  color: #eaf6ff;
  font-family: 'Inter', sans-serif;

  /* Glassmorphism card */
  background: rgba(8, 16, 32, 0.75);
  border: 1px solid rgba(0, 200, 255, 0.2);
  border-radius: 20px;
  box-shadow:
    0 0 40px rgba(0, 200, 255, 0.08),
    0 8px 32px rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(18px);
}

.feedback-view h1 {
  font-family: 'Orbitron', sans-serif;
  font-size: 26px;
  color: #00c8ff;
  text-shadow: 0 0 16px rgba(0, 200, 255, 0.5);
  margin-bottom: 28px;
  letter-spacing: 1px;
}

input,
select,
textarea {
  width: 100%;
  margin-top: 14px;
  padding: 12px 16px;
  font-size: 15px;
  border-radius: 10px;
  border: 1px solid rgba(0, 200, 255, 0.25);
  background: rgba(255, 255, 255, 0.05);
  color: #eaf6ff;
  box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
  outline: none;
}

input::placeholder,
textarea::placeholder {
  color: rgba(200, 230, 255, 0.45);
}

input:focus,
select:focus,
textarea:focus {
  border-color: rgba(0, 200, 255, 0.6);
  box-shadow: 0 0 12px rgba(0, 200, 255, 0.2);
  background: rgba(0, 200, 255, 0.06);
}

select {
  cursor: pointer;
  appearance: none;
}

select option {
  background: #0a1628;
  color: #eaf6ff;
}

textarea {
  height: 150px;
  resize: vertical;
}

.phone-input {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.phone-input select {
  flex: 1.5;
}

.phone-input input {
  flex: 2;
}

button {
  margin-top: 24px;
  padding: 13px 36px;
  font-size: 15px;
  font-weight: 700;
  font-family: 'Inter', sans-serif;
  background: linear-gradient(90deg, #00c8ff, #0077ff);
  color: #000;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  letter-spacing: 0.5px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 18px rgba(0, 200, 255, 0.3);
}

button:hover {
  transform: scale(1.04);
  background: linear-gradient(90deg, #00eaff, #0099ff);
  box-shadow: 0 6px 24px rgba(0, 200, 255, 0.45);
}

@media (max-width: 680px) {
  .feedback-view {
    margin: 90px 16px 60px;
    padding: 28px 20px;
  }

  .feedback-view h1 {
    font-size: 20px;
  }

  .phone-input {
    flex-direction: column;
    gap: 0;
  }

  .phone-input select,
  .phone-input input {
    flex: none;
    width: 100%;
  }
}
</style>